import { useCallback, useEffect, useState } from 'react';
import { getGrantOverview } from '../../../../../api/v2/OverviewApi';
import { downloadFile } from '../../../../../api/v2/FilesApi';
import { downloadFileByLink } from '../../../../shared/fileDownload';
import { GrantActivityItemDto } from '../../../../../dtos/GrantActivityItemDto';
import { useAppContext } from '../../../../shared/AppContext';

export interface OverviewControllerResult {
    items: GrantActivityItemDto[];
    loading: boolean;
    onFileClick: (fileId: string, fileName: string) => Promise<void>;
}

export function useOverviewController(grantId: string): OverviewControllerResult {
    const app = useAppContext();
    const [items, setItems] = useState<GrantActivityItemDto[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            const result = await getGrantOverview(grantId);
            if (result?.error) {
                app.errorToast(result.error.message);
            } else {
                setItems(result?.data ?? []);
            }
            setLoading(false);
        };
        load();
    }, [grantId, app]);

    const onFileClick = useCallback(async (fileId: string, fileName: string) => {
        app.showLoadPanel();

        // The browser opens a signed link and streams the file itself.
        if (app.configuration?.streamingFileTransfer) {
            const error = await downloadFileByLink(fileId, fileName);
            app.hideLoadPanel();
            if (error) app.errorToast(error);
            return;
        }

        const result = await downloadFile(fileId);
        if (result?.error) {
            app.errorToast(result.error.message);
        } else {
            const fileBlob = result?.data;
            if (fileBlob) {
                const link = document.createElement("a");
                const url = URL.createObjectURL(fileBlob);
                window.open(url, "_blank");
                link.href = url;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                setTimeout(() => URL.revokeObjectURL(url), 10000);
            } else {
                app.errorToast(`Failed to download: ${fileName}`);
            }
        }
        app.hideLoadPanel();
    }, [app]);

    return { items, loading, onFileClick };
}
